import React, { useState, useEffect } from 'react'
import { format, isToday } from 'date-fns'
import { Calendar, Users, CheckCircle, AlertCircle, MapPin } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { getTodayDateString, extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import { useCustodyParents, custodySideOf } from '@/hooks/useCustodyParents'

interface Child {
  id: string
  display_name: string
  username: string
}

interface Assignment {
  id: string
  chore: { name: string }
  assigned_to: string
  assigned_to_name?: string
  status: string
  points_offered: number
  due_date: string
}

interface UnifiedEvent {
  id: string
  type: string
  title: string
  date: string
  start_time?: string
  school?: string
  district?: string
  child?: string
  custody_parent?: string
}

interface TodaysSummaryProps {
  children: Child[]
  assignments: Assignment[]
}

export function TodaysSummary({ children, assignments }: TodaysSummaryProps) {
  const custodyParents = useCustodyParents()
  const [events, setEvents] = useState<UnifiedEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [todayStr, setTodayStr] = useState<string>('')

  useEffect(() => {
    loadTodaysData()
  }, [])

  useEffect(() => {
    getTodayDateString().then(setTodayStr)
  }, [])

  const loadTodaysData = async () => {
    try {
      setLoading(true)

      // Get today's date in family timezone
      const today = await getTodayDateString()

      // Fetch unified calendar for today
      const calendarResponse = await apiClient.get('/api/calendar/unified', {
        params: {
          start_date: today,
          end_date: today
        }
      })

      if (calendarResponse.events) {
        setEvents(calendarResponse.events)
      }
    } catch (error) {
      console.error('Failed to load today\'s data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading || custodyParents.loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    )
  }

  // Get today's assignments
  const todaysAssignments = assignments.filter(a => {
    if (!todayStr) return false
    // Extract date part using timezone-aware function
    const assignmentDate = extractDateInTimezoneSync(a.due_date)
    return assignmentDate === todayStr
  })

  const pendingChores = todaysAssignments.filter(a => a.status === 'pending')
  const completedChores = todaysAssignments.filter(
    a => a.status === 'completed' || a.status === 'verified' || a.status === 'pending_verification'
  )

  // Kids home today: custody events on the household side (the family's
  // first custody parent), whichever parent is signed in.
  const presenceData = events
    .filter(evt => evt.type === 'custody' && custodySideOf(evt.custody_parent, custodyParents) === 'primary')
    .map(evt => evt.child)
    .filter((name): name is string => Boolean(name))
  const kidsHome = children.filter(child =>
    presenceData.some(name => name?.toLowerCase() === child.display_name.toLowerCase())
  )
  const kidsAway = children.filter(child =>
    !presenceData.some(name => name?.toLowerCase() === child.display_name.toLowerCase())
  )

  // Get today's events (excluding custody)
  const todaysEvents = events.filter(evt => evt.type !== 'custody')
  const familyEvents = todaysEvents.filter(evt => evt.type === 'family')
  const schoolEvents = todaysEvents.filter(evt => evt.type === 'school')
  const districtEvents = todaysEvents.filter(evt => evt.type === 'district')

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Today's Summary</h2>
        <div className="text-sm text-gray-600">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Kids Home - Only show if we have custody data (presence tracking enabled) */}
        {events.some(evt => evt.type === 'custody') && (
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Users className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Kids Home</h3>
            </div>
            {kidsHome.length > 0 ? (
              <div className="space-y-1">
                {kidsHome.map(child => (
                  <div key={child.id} className="text-sm text-gray-700">
                    {child.display_name}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No children home today</p>
            )}
            {kidsAway.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-gray-500 mb-1">Away:</p>
                {kidsAway.map(child => (
                  <div key={child.id} className="text-xs text-gray-400">
                    {child.display_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chores */}
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-gray-900">Chores</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Pending:</span>
              <span className="text-sm font-semibold text-orange-600">
                {pendingChores.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Completed:</span>
              <span className="text-sm font-semibold text-green-600">
                {completedChores.length}
              </span>
            </div>
            {pendingChores.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-gray-500 mb-2">To Do:</p>
                <div className="space-y-1">
                  {pendingChores.slice(0, 3).map(chore => (
                    <div key={chore.id} className="text-xs text-gray-700 flex items-center space-x-1">
                      <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                      <span>{chore.chore.name}</span>
                      <span className="text-gray-400">- {chore.assigned_to_name}</span>
                    </div>
                  ))}
                  {pendingChores.length > 3 && (
                    <p className="text-xs text-gray-400">+{pendingChores.length - 3} more</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Events */}
        {todaysEvents.length > 0 && (
          <div className="bg-white rounded-lg p-4 md:col-span-2">
            <div className="flex items-center space-x-2 mb-3">
              <Calendar className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Today's Events</h3>
            </div>
            <div className="space-y-2">
              {familyEvents.map(evt => (
                <div key={evt.id} className="flex items-start space-x-2 text-sm">
                  <span className="inline-block w-2 h-2 mt-1.5 bg-blue-500 rounded-full"></span>
                  <div>
                    <div className="font-medium text-gray-900">{evt.title}</div>
                    {evt.start_time && (
                      <div className="text-xs text-gray-500">
                        {evt.start_time}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {schoolEvents.map(evt => (
                <div key={evt.id} className="flex items-start space-x-2 text-sm">
                  <span className="inline-block w-2 h-2 mt-1.5 bg-green-500 rounded-full"></span>
                  <div>
                    <div className="font-medium text-gray-900">{evt.title}</div>
                    {evt.school && (
                      <div className="text-xs text-gray-500">{evt.school}</div>
                    )}
                  </div>
                </div>
              ))}
              {districtEvents.map(evt => (
                <div key={evt.id} className="flex items-start space-x-2 text-sm">
                  <span className="inline-block w-2 h-2 mt-1.5 bg-purple-500 rounded-full"></span>
                  <div>
                    <div className="font-medium text-gray-900">{evt.title}</div>
                    {evt.district && (
                      <div className="text-xs text-gray-500">{evt.district}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
